import React, { useEffect, useState } from 'react';
import { logError } from '@/lib/error-handler';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Heart,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Lightbulb,
} from 'lucide-react';
import {
  accountHealthService,
  AccountHealthScore,
} from '@/services/analytics/accountHealthService';
import { cn } from '@/lib/utils';

interface AccountHealthCardProps {
  organizationId: string;
  compact?: boolean;
}

export function AccountHealthCard({ organizationId, compact = false }: AccountHealthCardProps) {
  const [healthScore, setHealthScore] = useState<AccountHealthScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadHealthScore();
  }, [organizationId]);

  const loadHealthScore = async () => {
    try {
      setLoading(true);
      let score = await accountHealthService.getHealthScore(organizationId);

      // If no score exists or it's older than 24 hours, calculate a new one
      if (!score || isScoreStale(score.last_calculated)) {
        score = await accountHealthService.calculateHealthScore(organizationId);
      }

      setHealthScore(score);
    } catch (error) {
      logError('Error loading health score:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const score = await accountHealthService.calculateHealthScore(organizationId);
      setHealthScore(score);
    } catch (error) {
      logError('Error refreshing health score:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const isScoreStale = (lastCalculated: string): boolean => {
    const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return new Date(lastCalculated) < dayAgo;
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'good':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'fair':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'poor':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'low':
        return 'text-green-600';
      case 'medium':
        return 'text-yellow-600';
      case 'high':
        return 'text-orange-600';
      case 'critical':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'declining':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      default:
        return <Minus className="w-4 h-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5" />
            Account Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-2 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!healthScore) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5" />
            Account Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No health data available.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="mt-2"
          >
            {refreshing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Calculate Score'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 border rounded-lg">
        <div className="flex items-center gap-2">
          <Heart className="w-4 h-4" />
          <span className="font-medium">{healthScore.overall_score}/100</span>
        </div>
        <Badge className={getHealthStatusColor(healthScore.health_status)}>
          {healthScore.health_status}
        </Badge>
        {getTrendIcon(healthScore.trend)}
        <Badge variant="outline" className={getRiskLevelColor(healthScore.risk_level)}>
          {healthScore.risk_level} risk
        </Badge>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5" />
            Account Health
          </CardTitle>
          <div className="flex items-center gap-2">
            {getTrendIcon(healthScore.trend)}
            <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Overall Score */}
        <div className="text-center">
          <div className="text-3xl font-bold mb-2">{healthScore.overall_score}/100</div>
          <Badge className={cn('mb-2', getHealthStatusColor(healthScore.health_status))}>
            {healthScore.health_status}
          </Badge>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <span>Risk Level:</span>
            <span className={cn('font-medium', getRiskLevelColor(healthScore.risk_level))}>
              {healthScore.risk_level}
            </span>
          </div>
        </div>

        {/* Risk Alert */}
        {healthScore.risk_level === 'high' ||
          (healthScore.risk_level === 'critical' && (
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                This account requires immediate attention due to {healthScore.risk_level} risk
                level.
              </AlertDescription>
            </Alert>
          ))}

        {/* Factor Scores */}
        <div className="space-y-3">
          <h4 className="font-medium text-sm">Health Factors</h4>

          {Object.entries(healthScore.factors).map(([factor, score]) => (
            <div key={factor} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="capitalize">{factor}</span>
                <span className="font-medium">{score}%</span>
              </div>
              <Progress
                value={score}
                className={cn(
                  'h-2',
                  score < 40 && 'text-red-500',
                  score >= 40 && score < 70 && 'text-yellow-500',
                  score >= 70 && 'text-green-500'
                )}
              />
            </div>
          ))}
        </div>

        {/* Recommendations */}
        {healthScore.recommendations.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              Recommendations
            </h4>
            <ul className="space-y-1">
              {healthScore.recommendations.slice(0, 3).map((rec, index) => (
                <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                  <CheckCircle className="w-3 h-3 mt-0.5 text-green-600 flex-shrink-0" />
                  {rec}
                </li>
              ))}
              {healthScore.recommendations.length > 3 && (
                <li className="text-xs text-muted-foreground">
                  +{healthScore.recommendations.length - 3} more recommendations
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Last Updated */}
        <div className="text-xs text-muted-foreground text-center">
          Last updated: {new Date(healthScore.last_calculated).toLocaleDateString()}
          {isScoreStale(healthScore.last_calculated) && (
            <span className="text-yellow-600 ml-1">(Stale - refresh recommended)</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
